import { appendTraceId, errorTraceId, stringifyError } from '@srm/core/errors';
import { IBackgroundJobStarted } from '@models/backgroundJobs/IBackgroundJob';
import { IFormData } from '@models/IFormData';
import { DocumentExecutionService } from '@services/actions/DocumentExecutionService';
import { FilesService } from '@services/FilesService';
import { BackgroundJobWaitStore } from '@services/backgroundJobs/awaitBackgroundJob';
import { BackgroundJobTask, createBackgroundJobTask } from '@services/backgroundJobs/backgroundJobTask';

export interface ICreateReportRequest {
    reportFormat?: string;
    /** Значения формы отчёта (FormTemplate); в карточку документа они не сохраняются. */
    formData?: IFormData;
}

// Интерцептор отклоняет массивом строк с прикреплённым кодом обращения: у такого отказа
// нет ни response, ни message, поэтому прежний разбор всегда схлопывался в String(error)
// и терял и текст бэка, и код. Тело ответа читаем по-прежнему — на случай сырого
// axios-отказа, всё остальное отдаём общему stringifyError.
export const createReportErrorText = (error: unknown): string => {
    const data = (error as { response?: { data?: unknown } })?.response?.data;
    const fromResponse = typeof data === 'string' ? data : (data as { message?: unknown })?.message;

    return typeof fromResponse === 'string' && fromResponse.trim()
        ? appendTraceId(fromResponse.trim(), errorTraceId(error), '\n')
        : stringifyError(error);
};

/**
 * Отчёт одним запросом: файл скачивается ответом. notifyOnError: false — ошибку показывает окно, без дубля-тоста.
 */
export const downloadReport = async (
    docId: string,
    actionKey: string,
    { reportFormat, formData }: ICreateReportRequest,
): Promise<void> => {
    if (formData) {
        await FilesService.downloadFilePost(
            `documentExecution/downloadReportDoc/${docId}/${actionKey}`,
            { reportFormat, formData },
            undefined,
            undefined,
            { notifyOnError: false },
        );
        return;
    }
    await FilesService.downloadFile(
        `documentExecution/downloadReportDoc/${docId}/${actionKey}/${reportFormat ?? ''}`,
        undefined,
        { notifyOnError: false },
    );
};

export type CreateReportTask = (request: ICreateReportRequest) => BackgroundJobTask;

export interface IBackgroundReportTaskDeps {
    store?: BackgroundJobWaitStore;
    start?: (request: ICreateReportRequest) => Promise<IBackgroundJobStarted>;
    download?: (request: ICreateReportRequest) => Promise<void>;
    /**
     * Фона нет (404 старта, 400 `runInBackground`): отчёт сформирован одним запросом и уже скачан — окно закрывается,
     * как без фона.
     */
    onDownloaded?: () => void;
}

/** Отчёт фоновой задачей `documentReport`; выключенный фон — одним запросом, файл сразу. */
export const createBackgroundReportTask =
    (
        docId: string,
        actionKey: string,
        {
            store,
            start = (request) => new DocumentExecutionService(docId).startReportInBackground(actionKey, request),
            download = (request) => downloadReport(docId, actionKey, request),
            onDownloaded,
        }: IBackgroundReportTaskDeps = {},
    ): CreateReportTask =>
    (request) =>
        createBackgroundJobTask(() => start(request), {
            kind: 'documentReport',
            store,
            resultActions: false,
            fallback: async () => {
                try {
                    await download(request);
                } catch (error) {
                    return { success: false, message: createReportErrorText(error) };
                }
                onDownloaded?.();
                return { success: true, message: '' };
            },
        });
